import rules from '../constants/rules';
import moment from 'moment';
import config from '../constants/config';
import ids from '../constants/ids';
import questionFunctions from './question';
import claimBankDetailsQuestionCodes from '../constants/claimBankDetailsQuestionsCodes';
import { store } from '../../store/store';
import HEADER_FUNCTIONS from './headers';
import codes from '../constants/codes';
import { Attachment } from '../constants/interfaces';
import * as actions from '../../actions';
import { BANK_DETAILS_TAB, SURVEY_TAB, TABS } from '../constants/tabs';
import { BlobServiceClient } from '@azure/storage-blob';
import { ObjectOfAny, QuestionSchema } from '../../reducers/types';
import { CurrencySchema } from '../../actions';
import i18n from '../../i18n/i18n';
import {
    formatAmountForInput,
    formatAmountWithCurrency,
} from './decimalFormatting';

type PropGeneralFunction = {
    getCurrencySymbol: (currencyId: string) => string;
    formattedTime: (date: any, splitter?: string) => string;
    formattedDate: (date: any, splitter?: string) => string;
    adjustMinMaxToLocalTime: (date: Date) => Date;
    parseAnswerType: (
        question: { [p: string]: any },
        answer: any,
        questionType: { [p: string]: any },
        currencyOptions: CurrencySchema[],
    ) => { [x: number]: any } | undefined;
    getAnswersForm: (
        question: { [p: string]: any },
        filterQuestionsById: { [p: string]: any },
        claimApplication: { [p: string]: any },
        questionTypesById: { [p: string]: any },
        currencyOptions: CurrencySchema[],
    ) => { [p: string]: any };
    getAnswersOfQuestionTree: (
        question: { [p: string]: any },
        filteredQuestionsById: { [p: string]: any },
        claimApplication: { [p: string]: any },
        questionTypesById: { [p: string]: any },
        currencyOptions: CurrencySchema[],
    ) => { [p: string]: any }[];
    downloadURI: (blob: any | Blob, name: string) => void;
    progressDownloadIndicator: (
        response: any,
        setProgress: Function,
        id: string,
    ) => Promise<void>;
    fileUploadAzureBlob: (
        attachment: any,
        file: any,
        setProgress: any,
        abortController: any,
        hasMetaDataSet: boolean,
    ) => Promise<string>;
    requestToUploadFileToBlob: (attachment: any) => Promise<any>;
    getEnvironmentByWindowUrl: () => string;
    findActiveTabToClaimTabArea: (query: any) => boolean | undefined;
    filterObjectBy: (obj: any, predicate: any) => { [p: string]: any };
    timeFormat: (time: string | any) => string;
    removeHyphens: (value: string) => string;
    addHyphenInEveryTwoCharacters: (value: any) => string;
    createClaimApplicationBankAccountDto: (
        answers: any,
        claimApplicationId: string,
    ) => any;
    appendCurrencySign: (
        amount: number | string,
        currency: { position: string; sign: string },
    ) => any | string;
    appendCurrencySymbolAndFormat: (
        amount: number | string,
        symbol: string | undefined,
    ) => any | string;
    rangeArray: (start: any, end: any) => any[];
    mergeArraysUnique: (...input: any) => any[];
    normalizeById: (
        id: string,
        entity: any,
    ) => { byId: {} | any; allIds: any[] };
    isUndefined: (v: any) => boolean;
    toTitleCase: (value: any) => any;
    mergeArraysOfObjects: (key: any, ...arraysWithObjects: any) => any[];
    extractDataFromResponseObject: (response: any) => any;
    compressImageDimensions: (
        imageFile: any,
        sizeLimit?: number,
    ) => Promise<File>;
    base64FileSizeInBytes: (base64String: any) => number;
    scaleImage: (
        encodedImage: File,
        fileType: string,
        scaleFactor: any,
    ) => Promise<any>;
    datURLtoFile: (dataurl: string, filename: string, fileType: string) => File;
    objectKeepDiffsAndClearSameValuesForBankDetails: (
        newObj: any,
        prevObj: any,
    ) => {} | any;
    getDifferenceFromStrings: (str1: string, str2: string) => string;
    createUrlQueryParameters: (params: any) => string;
    constructSingleQueryParameter: ({
        key,
        value,
    }: {
        key: any;
        value: any;
    }) => string | any;
    isBoolean: (v: any) => boolean;
    getBaseURL: () => string;
    validateFileExtensions: (file: string) => string;
    getInputItem: (
        variant: string,
        question: any,
        isReadOnlyOrEditableQuestion: string | undefined,
    ) => string;
    dateFormat: (date: moment.MomentInput) => string;
    validationCreatorForAttachments: (
        question: any,
    ) => (values: any, errors: any) => (value: any) => string | any;
    validationCreator: (
        question: any,
    ) => (
        questionValidationRules: any,
        values?: {} | any,
        questions?: any[],
        isReadOnlyQuestionHandler?: boolean,
    ) => (value: any) => null | any;
    getTimeDifference: (date: Date, date2: Date) => any;
    validateCommunicatorComments: (values: {
        files: { [p: string]: any } | Array<any>;
        content: any;
    }) => any;
    sizeOfFiles: (
        file: any,
        {
            option,
            setFieldError,
            formikBag,
            field,
            rejectedFile,
        }: {
            option: any;
            setFieldError: any;
            formikBag: any;
            field: string;
            rejectedFile: any;
        },
    ) => number;
    fromCSharpRegexString: (regexString: string) => null | any;
    findAttachmentsToMessageCommunicator: (field: string) => any;
    findTableContent: (field: string) => any | null;
    readFileAsBase64Async: (file: any) => Promise<File>;
    requestToDownloadFile: (
        id: string | any,
        name: string | undefined | null,
        type: string,
        setProgress?: Function,
    ) => Promise<any>;
    downLoadFile: (name: string | null | undefined, data: any) => any;
    isAndroidDevice: () => boolean;
    requestToUploadFile: (attachment: Attachment) => Promise<string | null>;
    requestToRemoveFile: (attachmentId: string) => Promise<string | null>;
    matchClaimIdFromUrl: () => void;
    hasCompleteMetaData: (
        attachment: any,
        setProgress: any,
        hasMetaDataSet: boolean,
    ) => void;
    requestFileDownload: (
        data: any,
        name: string,
        setProgress: any,
        id: string,
    ) => Promise<any>;
    isValidFileName: (fileName: string) => boolean;
    convertToParsedDateStringFormat: (inputDate: string) => string | Date;
    isValidDateFormat: (dateString: string) => boolean;
    capitalizeFirstLetter: (str: string) => string;
    formatValuesForRadioQuestion: (
        values: any,
        questions: QuestionSchema[],
    ) => ObjectOfAny;
    checkIfParentQuestionCheckboxTickedToEnableQuestion: (
        findParentQuestion?: QuestionSchema,
        values?: any,
    ) => Boolean | undefined;
    checkIfParentQuestionCheckboxUntickedToClearChildQuestionValue: (
        findParentQuestion?: QuestionSchema,
        values?: any,
    ) => Boolean | undefined;
    getCountryByWindowUrl: (countryCode: string) => string;
    fetchTermsAndConditions: (selectdLanguage: string) => Promise<any>;
    validateRequiredDateInputs: () => void;
    formatNumbers: (question: any) => string;
};
const GENERAL_FUNCTIONS: PropGeneralFunction = {} as any;

GENERAL_FUNCTIONS.getBaseURL = () => {
    const vpath = process.env.REACT_APP_API_VPATH
        ? process.env.REACT_APP_API_VPATH + '/'
        : '';
    return process.env.REACT_APP_BASE_URL + vpath;
};
GENERAL_FUNCTIONS.validateFileExtensions = file => {
    let fileExtension = file.substr(file.lastIndexOf('.') + 1);
    return '.' + fileExtension;
};

GENERAL_FUNCTIONS.getInputItem = (
    variant,
    question,
    isReadOnlyOrEditableQuestion,
) => {
    if (isReadOnlyOrEditableQuestion === codes.isReadOnly) {
        return 'plain';
    }
    if (question.displayType === 'range') {
        return 'buttonRadioGroup';
    }

    if (
        variant === 'list' &&
        (question.displayType === 'horizontal-radio' ||
            question.displayType === 'vertical-radio')
    ) {
        return 'radioButtonGroup';
    }
    if (questionFunctions.isQuestionOfTypeIntegerRange(question, variant)) {
        return 'select';
    }

    if (variant === 'text-input' || variant === 'number') {
        return 'text';
    }

    if (variant === 'text-area') {
        return 'textarea';
    }

    if (variant === 'date' || variant === 'date-time' || variant === 'time') {
        return 'date';
    }

    if (variant === 'checkbox') {
        return 'checkbox-inline';
    }

    if (variant === 'list') {
        return 'select';
    }

    if (variant === 'amount') {
        return 'currency';
    }

    if (variant === 'post-code') {
        return 'postcodeSearchableList';
    }

    if (variant === 'range') {
        return 'select';
    }

    return 'text';
};

GENERAL_FUNCTIONS.dateFormat = (date: moment.MomentInput) =>
    moment(date).format('DD/MM/YYYY');

GENERAL_FUNCTIONS.validationCreatorForAttachments =
    question => (values, errors) => value => {
        if (
            (question &&
                question.documentTypes &&
                question.documentTypes.some(
                    (i: { isRequired: boolean }) => i.isRequired,
                ) &&
                !values[question.code] &&
                // eslint-disable-next-line no-mixed-operators
                !value) ||
            (question &&
                question.documentTypes &&
                question.documentTypes.some(
                    (i: { isRequired: any }) => i.isRequired,
                ) &&
                values[question.code] &&
                values[question.code].length === 0 &&
                // eslint-disable-next-line no-mixed-operators
                errors &&
                !errors['dragDrop'])
        ) {
            return 'Please ensure the claim form is uploaded and labelled accordingly';
        } else {
            return errors['dragDrop'];
        }
    };

GENERAL_FUNCTIONS.validationCreator =
    question =>
        (
            questionValidationRules,
            values = {},
            questions = [],
            isReadOnlyQuestionHandler,
        ) =>
            value => {
                if (!questionValidationRules || isReadOnlyQuestionHandler) {
                    return null;
                }
                if (!question.isRequired && !value) {
                    return null;
                }

                // return the required error message when the question is required and not
                // answered
                if (question.isRequired && question.requiredErrorMessage && !value) {
                    return question.requiredErrorMessage;
                }

                const invalidRule = questionValidationRules.find(
                    (rule: { regExExpression: string | RegExp; code: string }) => {
                        // regex described rule
                        if (rule.regExExpression) {
                            // const regexString = GENERAL_FUNCTIONS
                            //     .fromCSharpRegexString(rule.regExExpression);
                            return RegExp(rule.regExExpression).test(value) === false;
                        }

                        // get question answer restrictions
                        const { maxRestrictionValue, minRestrictionValue } =
                            questionFunctions.getQuestionValidationRuleMinAndMaxValue(
                                question,
                                rule.code,
                                values,
                            );

                        // handle different values rule
                        if (
                            rule.code.toLowerCase() ===
                            rules.differentValues.code.toLowerCase()
                        ) {
                            const valueRestrictions = question.questionValidationRules[
                                rule.code
                            ]
                                ? question.questionValidationRules[rule.code].map(
                                    (rule: { value: any }) => rule.value,
                                )
                                : [];

                            return valueRestrictions.every((code: string | number) =>
                                values[code] ? values[code] === value : true,
                            );
                        }

                        // handle min age validation rule
                        if (
                            rule.code.toLowerCase() === rules.minAge.code.toLowerCase()
                        ) {
                            const diff = GENERAL_FUNCTIONS.getTimeDifference(
                                new Date(),
                                new Date(value),
                            );
                            return diff.years < parseInt(maxRestrictionValue);
                        }

                        // handle max age validation rule
                        if (
                            rule.code.toLowerCase() === rules.maxAge.code.toLowerCase()
                        ) {
                            const diff = GENERAL_FUNCTIONS.getTimeDifference(
                                new Date(),
                                new Date(value),
                            );
                            return diff.years > parseInt(minRestrictionValue);
                        }

                        // handle min value validation rule
                        if (
                            rule.code.toLowerCase() ===
                            rules.minValue.code.toLowerCase()
                        ) {
                            return parseFloat(value) < parseFloat(maxRestrictionValue);
                        }

                        // handle max value validation rule
                        if (
                            rule.code.toLowerCase() ===
                            rules.maxValue.code.toLowerCase()
                        ) {
                            return parseFloat(value) > parseFloat(minRestrictionValue);
                        }

                        // handle min length validation rule
                        if (
                            rule.code.toLowerCase() ===
                            rules.minLength.code.toLowerCase()
                        ) {
                            return (
                                value && value.length < parseInt(maxRestrictionValue)
                            );
                        }

                        // handle max length validation rule
                        if (
                            rule.code.toLowerCase() ===
                            rules.maxLength.code.toLowerCase()
                        ) {
                            return (
                                value && value.length > parseInt(minRestrictionValue)
                            );
                        }

                        // handle future date validation rule
                        if (
                            rule.code.toLowerCase() ===
                            rules.futureDate.code.toLowerCase()
                        ) {
                            return moment().isSameOrAfter(moment(value), 'day');
                        }

                        //handle past-date validation rule
                        if (
                            rule.code.toLowerCase() ===
                            rules.pastDate.code.toLowerCase()
                        ) {
                            return moment().isSameOrBefore(moment(value), 'day');
                        }

                        // handle today - future date validation rule
                        if (
                            rule.code.toLowerCase() ===
                            rules.todayFutureDate.code.toLowerCase()
                        ) {
                            return moment().isAfter(moment(value), 'day');
                        }

                        // handle today - past date validation rule
                        if (
                            rule.code.toLowerCase() ===
                            rules.todayPastDate.code.toLowerCase()
                        ) {
                            return moment().isBefore(moment(value), 'day');
                        }

                        // handle specific - equal or past date related to another question date validation rule
                        if (
                            values &&
                            rule.code.toLowerCase() ===
                            rules.beforeEqualSpecificDate.code.toLowerCase() &&
                            minRestrictionValue
                        ) {
                            return moment(value).isAfter(minRestrictionValue, 'day');
                        }

                        // handle  past date validation rule
                        if (
                            rule.code.toLowerCase() ===
                            rules.beforeSpecificDate.code.toLowerCase() &&
                            values &&
                            minRestrictionValue
                        ) {
                            return moment(value).isSameOrAfter(
                                moment(minRestrictionValue),
                                'day',
                            );
                        }

                        // handle specific - equal or past date related to another question date validation rule
                        if (
                            values &&
                            rule.code.toLowerCase() ===
                            rules.afterEqualSpecificDate.code.toLowerCase() &&
                            maxRestrictionValue
                        ) {
                            return moment(value).isBefore(maxRestrictionValue, 'day');
                        }

                        // handle  past date validation rule
                        if (
                            rule.code.toLowerCase() ===
                            rules.afterSpecificDate.code.toLowerCase() &&
                            values &&
                            maxRestrictionValue
                        ) {
                            return moment(value).isSameOrBefore(
                                moment(maxRestrictionValue),
                                'day',
                            );
                        }

                        // handle future date validation rule
                        if (
                            rule.code.toLowerCase() ===
                            rules.pastSpecificDate.code.toLowerCase() &&
                            values &&
                            minRestrictionValue
                        ) {
                            return moment(value).isAfter(
                                moment()
                                    .add(parseInt(minRestrictionValue), 'days')
                                    .format(),
                                'day',
                            );
                        }

                        // handle specific - future date validation rule
                        if (
                            rule.code.toLowerCase() ===
                            rules.futureSpecificDate.code.toLowerCase() &&
                            values &&
                            maxRestrictionValue
                        ) {
                            return moment(value).isBefore(
                                moment()
                                    .add(parseInt(maxRestrictionValue), 'days')
                                    .format(),
                                'day',
                            );
                        }

                        return false;
                    },
                );

                if (!invalidRule) {
                    return null;
                }

                // if answer is given and is invalid and there is a specific
                // error message associated with the question then display this message
                if (
                    value &&
                    invalidRule &&
                    question.questionValidationRules &&
                    question.questionValidationRules[invalidRule.code] &&
                    question.questionValidationRules[invalidRule.code].length > 0 &&
                    question.questionValidationRules[invalidRule.code].some(
                        (item: { errorMessage: string }) => item.errorMessage,
                    )
                ) {
                    return question.questionValidationRules[invalidRule.code].find(
                        (item: { errorMessage: string }) => item.errorMessage,
                    ).errorMessage;
                }

                // if answer is given and is invalid and there is not a specific
                // error message associated with the question then display the generic
                // question validation rule message
                if (value && invalidRule) {
                    return invalidRule.description;
                }

                return null;
            };

GENERAL_FUNCTIONS.validateCommunicatorComments = (values: any) => {
    let errors: { generals: string } = {} as any;
    let fileValidation: any = Object.values(values.files)
        .filter((e: any) => e.file)
        .map((e: any) => ({ file: e.file.name }));

    if (
        (values.content && values.content.trim().length === 0) ||
        (!fileValidation && fileValidation.length === 0)
    ) {
        errors.generals = i18n.t('commounicatorEmtyContentWithoutImage');
    }
    if (
        values.content &&
        values.content.trim().length === 0 &&
        fileValidation.length > 0
    ) {
        errors.generals = i18n.t('commounicatorEmtyContentWithImage');
    }
    if (!values.content || (!fileValidation && fileValidation.length === 0)) {
        errors.generals = i18n.t('commounicatorEmtyContentWithoutImage');
    }

    if (!values.content && fileValidation.length > 0) {
        errors.generals = i18n.t('commounicatorEmtyContentWithImage');
    }

    return errors;
};

GENERAL_FUNCTIONS.getTimeDifference = (firstDate: any, secondDate: any) => {
    const elapsedTimeFromNow = Math.abs(firstDate - secondDate);
    const elapsedTimeInYears =
        elapsedTimeFromNow / 1000 / 60 / 60 / 24 / 365.25;

    return {
        years: elapsedTimeInYears,
    };
};

// function that returns size of Files
//the options are option: 0 or 1
//the options are setFieldError function
//the options are formikBag function
//the options are field show the error
// return rejectedFile
GENERAL_FUNCTIONS.sizeOfFiles = (
    file,
    { option, setFieldError, formikBag, field, rejectedFile },
) => {
    let numberOfFiles = 0;
    let FileSize = file.size;
    let isValidFile = GENERAL_FUNCTIONS.isValidFileName(file.name);
    if (FileSize === 0 && option === 1) {
        numberOfFiles++;
        rejectedFile.push(file);
        formikBag.setFieldError(`${field}`, i18n.t('emptyFilesMessage'));
    } else if (FileSize === 0 && option === 0) {
        rejectedFile.push(file);
        setFieldError(`${field}`, i18n.t('emptyFilesMessage'));
        numberOfFiles++;
    } else if (FileSize > 1024 * 1024 * 100 && option === 0) {
        numberOfFiles++;
        rejectedFile.push(file);
        setFieldError(`${field}`, i18n.t('fileSizeExceedMessage'));
    } else if (FileSize > 1024 * 1024 * 100 && option === 1) {
        numberOfFiles++;
    } else if (!isValidFile && option === 0) {
        numberOfFiles++;
        rejectedFile.push(file);
        setFieldError(
            `${field}`,
            i18n.t('fileInvalidMessage', { fileName: file.name }),
        );
    } else if (!isValidFile && option === 1) {
        numberOfFiles++;
        rejectedFile.push(file);
        formikBag.setFieldError(
            `${formikBag.field.name}`,
            i18n.t('fileInvalidMessage', { fileName: file.name }),
        );
    } else {
        numberOfFiles = 0;
    }
    return numberOfFiles;
};

GENERAL_FUNCTIONS.fromCSharpRegexString = regexString => {
    if (!regexString) return null;

    if (
        regexString &&
        regexString.length > 3 &&
        regexString[0] === '@' &&
        regexString[1] === '"' &&
        regexString[regexString.length - 1] === '"'
    ) {
        return regexString.slice(2, regexString.length - 1);
    }

    return regexString;
};

GENERAL_FUNCTIONS.findAttachmentsToMessageCommunicator = (field: any) => {
    const pattern = new RegExp(/{(\s*?.*?)*?}/g);
    const patternForAttachments = new RegExp(/<attachment/g);
    let [, filename, id, description] = [null, null, null, null] as any;
    const regex =
        /<attachment filename\s*=\s*{([\w-]+)}\s*id\s*=\s*\{([\s\S]+?)\}\s*description\s*=\s*{([\s\S]+?)}>/m;
    let matchWhiteSpaces = regex.exec(field);
    if (matchWhiteSpaces) {
        [, filename, id, description] = matchWhiteSpaces;
        return { filename, id, description };
    }
    //in case the first match does not match special regex
    if (!matchWhiteSpaces && patternForAttachments.test(field)) {
        return {
            filename: field.match(pattern)[0]?.replace(/\{|\}/gi, ''),
            id: field.match(pattern)[1]?.replace(/\{|\}/gi, ''),
            description: field.match(pattern)[2]?.replace(/\{|\}/gi, ''),
        };
    }
};

GENERAL_FUNCTIONS.findTableContent = field => {
    let attachmentTable = new RegExp('table');
    if (attachmentTable.test(field)) {
        let test = /.*?/;
        let tables = field.match(test);
        for (let key in tables) {
            if (key === 'input') {
                return tables[key];
            }
        }
    } else {
        return null;
    }
};

GENERAL_FUNCTIONS.readFileAsBase64Async = file => {
    return new Promise((resolve, reject) => {
        let reader: any = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
            resolve(
                reader.result.substring(reader.result.indexOf('base64,') + 7),
            );
        };

        reader.onerror = reject;
    });
};

GENERAL_FUNCTIONS.requestToDownloadFile = async (
    id,
    name,
    type,
    setProgress,
) => {
    const token = store.getState().applicationUser.accessToken;
    let url =
        type === codes.attachment
            ? `${GENERAL_FUNCTIONS.getBaseURL()}api/Attachment/download/${id}`
            : type === codes.template
                ? `${GENERAL_FUNCTIONS.getBaseURL()}api/ClaimApplicationTemplate/${id}`
                : null;
    if (url === null) {
        return null;
    }
    try {
        const result = await fetch(url, {
            method: 'GET',
            headers: HEADER_FUNCTIONS.headersWithTokenJson(token),
        });
        const file = await result.json();

        if (file && file.data) {
            return await GENERAL_FUNCTIONS.requestFileDownload(
                file.data,
                name as string,
                setProgress,
                id,
            );
        } else if (file && file.warnings) {
            store.dispatch(
                actions.displayDownloadFileWarning({
                    body: { warnings: file.warnings },
                }),
            );
            setTimeout(() => {
                // @ts-ignore
                store.dispatch(actions.hideMessage(undefined));
            }, 2000);
            return codes.warnings;
        } else if (result.status === 404) {
            store.dispatch(
                actions.displayDownloadFileWarning({
                    body: { warnings: [codes.fileNotFound] },
                } as any),
            );
            setTimeout(() => {
                // @ts-ignore
                store.dispatch(actions.hideMessage(undefined));
            }, 2000);

            return codes.fileNotFound;
        } else if (!result) {
            store.dispatch(
                actions.displayDownloadFileWarning({
                    body: { warnings: [codes.fileNotFound] },
                } as any),
            );
            return codes.fileNotFound;
        }
    } catch (e) {
        store.dispatch(
            actions.displayDownloadFileWarning({
                body: { warnings: [codes.blankPageFiles] },
            } as any),
        );
        setTimeout(() => {
            // @ts-ignore
            store.dispatch(actions.hideMessage(undefined));
        }, 2000);
        return codes.fileNotFound;
    }
};

GENERAL_FUNCTIONS.requestFileDownload = async (
    data: any,
    name: string,
    setProgress: any,
    id: string,
): Promise<any> => {
    // Download the file using the fetch API
    let sasUri = data && data?.uri ? data.uri : data;
    const response = await fetch(sasUri, { responseType: 'blob' } as any);
    await GENERAL_FUNCTIONS.progressDownloadIndicator(
        response?.clone(),
        setProgress,
        id as string,
    );

    if (!response.ok) {
        throw new Error('Failed to download file');
    }
    return await response?.blob();
};

GENERAL_FUNCTIONS.downloadURI = (blob, name) => {
    // Create a temporary anchor element to trigger the download
    const a = document.createElement('a');
    a.href = window.URL.createObjectURL(blob);
    a.download = name as string;
    document.body.appendChild(a);
    a.click();
    // Clean up the temporary anchor element
    window.URL.revokeObjectURL(a.href);
    document.body.removeChild(a);
};

GENERAL_FUNCTIONS.progressDownloadIndicator = async (
    response,
    setProgress,
    id,
) => {
    if (response) {
        const contentLength = response.headers.get('content-length');
        const total = parseInt(contentLength, 10);
        let downloaded = 0;
        const reader = response.body.getReader();
        let receivedLength = 0;
        while (true) {
            const { done, value } = await reader.read();
            if (done) {
                break;
            }
            if (contentLength !== null) {
                receivedLength += value.length;
                downloaded = receivedLength;
                let currentProgress = (downloaded / total) * 100;
                setProgress(id, currentProgress);
            }
        }
    }
};

GENERAL_FUNCTIONS.requestToUploadFile = async (attachment: Attachment) => {
    const token = store.getState().applicationUser.accessToken;
    try {
        const result = await fetch(
            `${GENERAL_FUNCTIONS.getBaseURL()}api/Attachment`,
            {
                method: 'POST',
                headers: HEADER_FUNCTIONS.headersWithTokenJson(token),
                body: JSON.stringify(attachment),
            },
        );
        const response = await result.json();
        if (response.errors) {
            store.dispatch(
                actions.handleBusinessErrors({
                    customMessage: response.errors,
                }),
            );
            return;
        }
        return response.data;
    } catch (e) {
        return null;
    }
};
GENERAL_FUNCTIONS.requestToUploadFileToBlob = async (attachment: any) => {
    const token = store.getState().applicationUser.accessToken;
    try {
        const result = await fetch(
            `${GENERAL_FUNCTIONS.getBaseURL()}api/Attachment/Prepare`,
            {
                method: 'POST',
                headers: HEADER_FUNCTIONS.headersWithTokenJson(token),
                body: JSON.stringify(attachment),
            },
        );
        const response = await result.json();
        if (response.errors) {
            store.dispatch(
                actions.handleBusinessErrors({
                    customMessage: response.errors,
                }),
            );
            return;
        }
        return response.data;
    } catch (e) {
        return null;
    }
};

GENERAL_FUNCTIONS.requestToRemoveFile = async (attachmentId: string) => {
    const token = store.getState().applicationUser.accessToken;
    try {
        const result = await fetch(
            `${GENERAL_FUNCTIONS.getBaseURL()}api/Attachment/${attachmentId}`,
            {
                method: 'DELETE',
                headers: HEADER_FUNCTIONS.headersWithTokenJson(token),
            },
        );
        const response = await result.json();
        if (response.errors) {
            store.dispatch(
                actions.handleBusinessErrors({
                    customMessage: response.errors,
                }),
            );
            return;
        }
        return response.data;
    } catch (e) {
        return null;
    }
};

GENERAL_FUNCTIONS.downLoadFile = (name, base64Encoding) => {
    const src = `data:application/binary;base64,${base64Encoding}`;
    // @ts-ignore
    if (window.navigator && window.navigator.msSaveOrOpenBlob) {
        const byteCharacters = atob(base64Encoding);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/binary' });
        // @ts-ignore
        window.navigator.msSaveOrOpenBlob(blob, name);
    } else {
        const downloadLink: any = document.createElement('a');
        document.body.appendChild(downloadLink);
        const fileName = name;
        downloadLink.href = src;
        downloadLink.download = fileName;
        downloadLink.click();
        document.body.removeChild(downloadLink);
    }
};

GENERAL_FUNCTIONS.datURLtoFile = (dataurl, filename, fileType) => {
    let bstr = atob(dataurl),
        n = bstr.length,
        u8arr = new Uint8Array(n);

    while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: fileType });
};

GENERAL_FUNCTIONS.scaleImage = async (encodedImage, fileType, scaleFactor) => {
    return new Promise(resolve => {
        const img = new Image();
        img.onload = () => {
            const scaledWidth = img.width * scaleFactor;
            const scaledHeight = img.height * scaleFactor;
            const canvasEl = document.createElement('canvas');
            canvasEl.width = scaledWidth;
            canvasEl.height = scaledHeight;
            const ctx: any = canvasEl.getContext('2d');
            ctx.drawImage(img, 0, 0, scaledWidth, scaledHeight);
            const dataUrlString = ctx.canvas.toDataURL(fileType, 1);
            resolve(
                dataUrlString.substring(dataUrlString.indexOf('base64,') + 7),
            );
            img.remove();
            canvasEl.remove();
        };
        img.src = `data:${fileType};base64,${encodedImage}`;
    });
};

GENERAL_FUNCTIONS.base64FileSizeInBytes = base64String => {
    return 4 * Math.ceil(base64String.length / 3) * 0.5624896334383812;
};

GENERAL_FUNCTIONS.compressImageDimensions = async (
    imageFile,
    sizeLimit = config.MAX_ALLOWED_SIZE_IN_BYTES,
) => {
    let iterativeEncodedImage =
        await GENERAL_FUNCTIONS.readFileAsBase64Async(imageFile);
    let totalIter = 1;
    while (
        GENERAL_FUNCTIONS.base64FileSizeInBytes(iterativeEncodedImage) >
        sizeLimit
    ) {
        iterativeEncodedImage = await GENERAL_FUNCTIONS.scaleImage(
            iterativeEncodedImage,
            imageFile.type,
            1 / Math.pow(2, totalIter),
        );
        totalIter = totalIter + 1;
    }
    return iterativeEncodedImage;
};

GENERAL_FUNCTIONS.extractDataFromResponseObject = response => {
    return response && response.body && response.body.data
        ? response.body.data
        : null;
};

GENERAL_FUNCTIONS.mergeArraysOfObjects = (key, ...arraysWithObjects) => {
    const map: any = {};
    arraysWithObjects.forEach((arg: any[]) => {
        arg.forEach((item: any) => (map[item[key]] = item));
    });

    return Object.values(map).map(item => item);
};

GENERAL_FUNCTIONS.toTitleCase = (string: any): any => {
    string = string.toLowerCase().split(' ');
    for (let i = 0; i < string.length; i++) {
        string[i] = string[i].charAt(0).toUpperCase() + string[i].slice(1);
    }
    return string.join(' ');
};

GENERAL_FUNCTIONS.isUndefined = v => {
    return typeof v === 'undefined';
};

GENERAL_FUNCTIONS.normalizeById = (id, entity) => {
    if (!entity) {
        return { byId: {}, allIds: [] };
    }

    let byId = {};
    let allIds: any[] = [];

    entity.forEach((item: { [x: string]: any }) => {
        byId = {
            ...byId,
            [item[id]]: item,
        };
        allIds.push(item[id]);
    });

    return {
        byId: byId,
        allIds: allIds,
    };
};

GENERAL_FUNCTIONS.mergeArraysUnique = (...input) => {
    const merged = [].concat(...input);
    const newSet: any = new Set(merged);
    return [...newSet];
};

GENERAL_FUNCTIONS.rangeArray = (start, end) => {
    const range = [];
    for (let i = start; i <= end; i++) {
        range.push(i);
    }

    return range;
};

GENERAL_FUNCTIONS.appendCurrencySign = (amount, currency) => {
    if (!currency) {
        return amount;
    }

    if (currency.position === config.currencyPosition.after) {
        return `${amount} ${currency.sign}`;
    }

    if (currency.position === config.currencyPosition.before) {
        return `${currency.sign} ${amount}`;
    }

    return amount;
};

GENERAL_FUNCTIONS.appendCurrencySymbolAndFormat = (amount, symbol) => {
    return formatAmountWithCurrency(
        amount,
        symbol,
        store.getState().applicationUser.applicationUserDetails,
    );
};

GENERAL_FUNCTIONS.createClaimApplicationBankAccountDto = (
    answers,
    claimApplicationId,
) => {
    if (!answers) {
        return {};
    }
    if (
        answers[claimBankDetailsQuestionCodes.bankAccountType] ===
        ids.bankAccountTypeUkOption
    ) {
        return {
            BankAccountTypeId:
                answers[claimBankDetailsQuestionCodes.bankAccountType],
            AccountHolderName:
                answers[claimBankDetailsQuestionCodes.accoutnHolderNameUk],
            AccountNumber: answers[claimBankDetailsQuestionCodes.accountNumber],
            SortCode: answers[claimBankDetailsQuestionCodes.sortCode]
                ? answers[claimBankDetailsQuestionCodes.sortCode].replace(
                    /-/g,
                    '',
                )
                : null,
            ClaimApplicationId: claimApplicationId,
        };
    }

    if (
        answers[claimBankDetailsQuestionCodes.bankAccountType] ===
        ids.bankAccountTypeinternationalOption
    ) {
        return {
            BankAccountTypeId:
                answers[claimBankDetailsQuestionCodes.bankAccountType],
            AccountHolderName:
                answers[
                claimBankDetailsQuestionCodes.accoutnHolderNameInternational
                ],
            Iban: answers[claimBankDetailsQuestionCodes.iban],
            ClaimApplicationId: claimApplicationId,
        };
    }
};

GENERAL_FUNCTIONS.addHyphenInEveryTwoCharacters = value => {
    let removeAllDashesFromValue = value ? value.split('-').join('') : ''; // Remove dash (-) if mistakenly entered.

    return removeAllDashesFromValue
        ? removeAllDashesFromValue.match(/.{1,2}/g).join('-')
        : ''; //add dashes in every 2 characters
};

GENERAL_FUNCTIONS.removeHyphens = value => {
    return value ? value.replaceAll('-', '') : ''; // Remove dash (-) if mistakenly entered.
};

GENERAL_FUNCTIONS.filterObjectBy = (obj, predicate) => {
    return Object.keys(obj)
        .filter(key => predicate(obj[key]))
        .reduce((res, key) => Object.assign(res, { [key]: obj[key] }), {});
};

GENERAL_FUNCTIONS.timeFormat = time => moment(time).format('HH:mm');

GENERAL_FUNCTIONS.objectKeepDiffsAndClearSameValuesForBankDetails = (
    newObj,
    prevObj,
) => {
    let target = {};
    Object.keys(newObj).filter(i => {
        if (newObj[i] && prevObj[i] && newObj[i] !== prevObj[i]) {
            let obj: any = {};
            //find diff char
            obj[i] = GENERAL_FUNCTIONS.getDifferenceFromStrings(
                prevObj[i],
                newObj[i],
            );
            target = Object.assign(target, obj);
        } else {
        }
    });
    return target;
};

GENERAL_FUNCTIONS.getDifferenceFromStrings = (str1, str2) => {
    let diff = '';
    str2.split('').forEach(function (val, i) {
        if (val != str1.charAt(i)) diff += val;
    });
    return diff;
};

GENERAL_FUNCTIONS.createUrlQueryParameters = params => {
    return Object.entries(params).reduce((acc, [key, value], index) => {
        if (!key || !value) {
            return acc;
        }

        const constructedQueryParam =
            GENERAL_FUNCTIONS.constructSingleQueryParameter({ key, value });

        if (index === 0) {
            return acc + `?${constructedQueryParam}`;
        }

        return acc + `&${constructedQueryParam}`;
    }, '');
};

GENERAL_FUNCTIONS.constructSingleQueryParameter = ({ key, value }) => {
    if (!key || !value) {
        return '';
    }

    if (Array.isArray(value)) {
        return value.reduce((acc, curr, index) => {
            if (index === 0) {
                return acc + `${key}=${curr}`;
            }

            return acc + `&${key}=${curr}`;
        }, '');
    }

    return `${key}=${value}`;
};
GENERAL_FUNCTIONS.isBoolean = v => {
    return !!v === v;
};

GENERAL_FUNCTIONS.isAndroidDevice = () => {
    let ua = navigator.userAgent.toLowerCase();
    return ua.indexOf('android') > -1;
};

GENERAL_FUNCTIONS.getEnvironmentByWindowUrl = () => {
    const environmentName = process.env.REACT_APP_ENV_NAME;
    if (!environmentName) {
        return '';
    }

    const isEnvironmentIndicator = (
        codes.enviromentsForIndicator as Record<string, string>
    )[environmentName];
    return isEnvironmentIndicator ? isEnvironmentIndicator : '';
};

GENERAL_FUNCTIONS.getCountryByWindowUrl = countryCode => {
    const isCountryIndicator =
        codes.countries[countryCode as keyof typeof codes.countries];
    return isCountryIndicator ? isCountryIndicator : 'Unknown';
};

GENERAL_FUNCTIONS.findActiveTabToClaimTabArea = (query: any) => {
    let tabs: any = [
        TABS.communication.key,
        TABS.claimDetails.key,
        TABS.documents.key,
        BANK_DETAILS_TAB.key,
        SURVEY_TAB.key,
    ];
    if (tabs.includes(query)) return true;
};

GENERAL_FUNCTIONS.matchClaimIdFromUrl = () => {
    const path = window.location.pathname; // get the current URL path
    const pattern =
        /\/claims\/([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})/;
    const match = path.match(pattern);
    return match && match.length > 1 ? match[1] : null;
};

// @ts-ignore
GENERAL_FUNCTIONS.fileUploadAzureBlob = async (
    attachment: {
        attachmentId: string;
        blobURI: string;
        claimApplicationId: string;
        fileName: string;
    },
    file: Blob,
    setProgress: any,
    abortController: any,
    hasMetaDataSet: boolean,
) => {
    try {
        const parts = attachment.blobURI.split(/\s|\?/);
        const sasToken = parts[1];
        const segments = parts[0].split('/');
        const storageAccount = segments[2];
        const containerName = segments[3];
        let metadata: any = {
            state: 'uploaded',
            // fullFileName:attachment.fileName,
            createdDate: moment().format('YYYY-MM-DDTHH:mm:ss'),
            createdById:
                store.getState().applicationUser.applicationUserDetails?.id,
        };
        if (attachment.claimApplicationId) {
            metadata = {
                ...metadata,
                claimApplicationId: attachment.claimApplicationId,
            };
        }

        const blobService = new BlobServiceClient(
            `https://${storageAccount}?${sasToken}`,
        );
        const containerClient = blobService.getContainerClient(containerName);
        const blobClient = containerClient.getBlockBlobClient(
            attachment.attachmentId,
        );
        const options = {
            blobHTTPHeaders: {
                blobContentType: file.type,
            },
            blockSize: 4 * 1024 * 1024,
            concurrency: 20,
            onProgress: (progress: any) => {
                const currentProgress =
                    (progress.loadedBytes / file.size) * 100;
                if (currentProgress < 100 && !hasMetaDataSet) {
                    setProgress(
                        currentProgress,
                        attachment.attachmentId,
                        hasMetaDataSet,
                    );
                }
            },
            abortSignal: abortController.signal,
        };

        await blobClient.uploadData(file, options);

        // Try setting metadata
        try {
            await blobClient.setMetadata(metadata);
            hasMetaDataSet = true; // Set the flag to true
        } catch (metadataError) {
            //for metadata failure
        }
        GENERAL_FUNCTIONS.hasCompleteMetaData(
            attachment,
            setProgress,
            hasMetaDataSet,
        );
    } catch (error) {
        console.log(`cannot upload file , it return error ${error}`);
    }
};

GENERAL_FUNCTIONS.hasCompleteMetaData = (
    attachment: any,
    setProgress: any,
    hasMetaDataSet: boolean,
) => {
    if (hasMetaDataSet) {
        // Do something when both the upload and metadata setting are done
        console.log('Upload and metadata setting are complete');
        setProgress(100, attachment?.attachmentId, hasMetaDataSet);
    } else {
        // Do something if metadata setting failed
        console.log('Upload complete, but metadata setting failed');
        setProgress(99, attachment?.attachmentId, hasMetaDataSet); // or some other indicator
    }
};

GENERAL_FUNCTIONS.isValidFileName = (fileName: string) => {
    let rg1 = /^[^\\/:\*\?"<>\|]+$/; // forbidden characters \ / : * ? " < > |
    let rg2 = /^\./; // cannot start with dot (.)
    let rg3 = /^(nul|prn|con|lpt[0-9]|com[0-9])(\.|$)/i; // forbidden file names
    return rg1.test(fileName) && !rg2.test(fileName) && !rg3.test(fileName);
};

GENERAL_FUNCTIONS.getAnswersOfQuestionTree = (
    question: { [key: string]: any },
    filteredQuestionsById: { [key: string]: any },
    claimApplication: { [key: string]: any },
    questionTypesById: { [key: string]: any },
    currencyOptions: CurrencySchema[],
): { [key: string]: any }[] => {
    let items = [] as { [key: string]: any }[];
    const answerOfQuestion = claimApplication.answers.find(
        (a: { [key: string]: any }) => a.questionCode === question.code,
    );
    const questionType = questionTypesById[question.questionTypeId];
    items.push(
        questionFunctions.parseQuestionWithAnswer(
            question,
            answerOfQuestion,
            questionType,
            currencyOptions,
        ),
    );

    // if option is selected form a dropdown then we have to get all
    //sub question answers
    if (questionType && questionType.code === 'list' && answerOfQuestion) {
        const subQs = Object.values(filteredQuestionsById)
            .filter((q: { [key: string]: any }) => q.enabledByOptionId !== null)
            .filter(
                q =>
                    q.enabledByOptionId === answerOfQuestion.optionId &&
                    q.enabledByQuestionId ===
                    filteredQuestionsById[answerOfQuestion.questionCode].id,
            );
        if (subQs && subQs.length > 0) {
            subQs.forEach(sq => {
                items = [
                    ...items,
                    ...GENERAL_FUNCTIONS.getAnswersOfQuestionTree(
                        sq,
                        filteredQuestionsById,
                        claimApplication,
                        questionTypesById,
                        currencyOptions,
                    ),
                ];
            });
        }
    }

    return items;
};
GENERAL_FUNCTIONS.getAnswersForm = (
    question: { [key: string]: any },
    filterQuestionsById: { [key: string]: any },
    claimApplication: { [key: string]: any },
    questionTypesById: { [key: string]: any },
    currencyOptions: CurrencySchema[],
): { [key: string]: any } => {
    if (!question) return {};
    let items = {};
    const answerOfQuestion = claimApplication?.answers?.find(
        (a: { [key: string]: any }) => a.questionCode === question.code,
    );
    const questionType = questionTypesById[question.questionTypeId];
    if (answerOfQuestion?.questionCode) {
        items = {
            ...GENERAL_FUNCTIONS.parseAnswerType(
                question,
                answerOfQuestion,
                questionType,
                currencyOptions,
            ),
        };
    }
    // if option is selected form a dropdown then we have to get all
    //sub question answers
    if (
        questionType &&
        questionType.code === 'list' &&
        answerOfQuestion &&
        answerOfQuestion.optionId
    ) {
        const subQs = Object.values(filterQuestionsById).filter(
            (q: { [key: string]: any }) =>
                q.enabledByOptionId === answerOfQuestion.optionId &&
                q.enabledByQuestionId ===
                filterQuestionsById[answerOfQuestion.questionCode].id,
        );

        if (subQs) {
            subQs.forEach(sq => {
                items = {
                    ...items,
                    ...GENERAL_FUNCTIONS.getAnswersForm(
                        sq,
                        filterQuestionsById,
                        claimApplication,
                        questionTypesById,
                        currencyOptions,
                    ),
                };
            });
        }
    }
    return items;
};
GENERAL_FUNCTIONS.parseAnswerType = (
    question: ObjectOfAny,
    answer: ObjectOfAny | any,
    questionType: ObjectOfAny,
    currencyOptions: CurrencySchema[],
): { [x: number]: any } | undefined => {
    if (!answer && answer.questionCode) {
        return { [answer.questionCode]: null };
    }

    if (answer.isAnonymised) {
        return { [answer.questionCode]: codes.Anonymised };
    }

    if (
        questionType.code === 'text-input' ||
        questionType.code === 'text-area' ||
        questionType.code === 'post-code'
    ) {
        return { [answer.questionCode]: answer.text };
    }

    if (questionType.code === 'amount') {
        return {
            [answer.questionCode]: formatAmountForInput(
                answer.amount,
                store.getState().applicationUser.applicationUserDetails,
            ),
        };
    }

    if (questionType.code === 'number') {
        return { [answer.questionCode]: answer.amount };
    }

    if (
        questionType.code === 'date' ||
        questionType.code === 'date-time' ||
        questionType.code === 'time'
    ) {
        return {
            [answer.questionCode]: moment(answer.date).format('DD/MM/YYYY'),
        };
    }

    if (questionType.code === 'list') {
        const selectedOption = question.options.find(
            (o: { id: string }) => o.id === answer.optionId,
        );
        return {
            [answer.questionCode]: selectedOption ? selectedOption.name : null,
        };
    }

    if (questionType.code === 'user-list') {
        const selectedOption = question.options.find(
            (o: { id: string }) => o.id === answer.selectedUserId,
        );
        return {
            [answer.questionCode]: selectedOption ? selectedOption.name : null,
        };
    }

    if (questionType.code === 'checkbox') {
        return { [answer.questionCode]: answer.flag ? 'yes' : 'no' };
    }
};

GENERAL_FUNCTIONS.isValidDateFormat = (dateString: string): boolean => {
    // Regular expression for 'dd/MM/yyyy' format
    const dateFormatRegex = /^\d{2}\/\d{2}\/\d{4}$/;
    return dateFormatRegex.test(dateString);
};

GENERAL_FUNCTIONS.convertToParsedDateStringFormat = (
    inputDate: string,
): string | Date => {
    // Parse the input date string
    if (GENERAL_FUNCTIONS.isValidDateFormat(inputDate)) {
        const [day, month, year] = inputDate.split('/');
        return new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
    } else {
        return inputDate;
    }
};
GENERAL_FUNCTIONS.adjustMinMaxToLocalTime = (date: Date) => {
    // Adjust maxDate to match the local time zone
    const convertedMaxDate = new Date();
    if (date) {
        const localTimeZoneOffset = (date as any).getTimezoneOffset();
        convertedMaxDate.setMinutes(
            (date as any).getMinutes() - localTimeZoneOffset,
        );
    }
    return date;
};

GENERAL_FUNCTIONS.capitalizeFirstLetter = (str: string) => {
    return str.charAt(0).toUpperCase() + str.slice(1);
};

GENERAL_FUNCTIONS.formatValuesForRadioQuestion = (
    values: any,
    questions: QuestionSchema[],
) => {
    let formattedValues = { ...values };
    // Only modify the values related to the questions
    questions.forEach((question: QuestionSchema) => {
        if (values[question.code]) {
            const splitValues = values[question.code].split('//');
            formattedValues[question.code] =
                splitValues.length > 1 ? splitValues[1] : null;
        }
    });
    return formattedValues;
};

GENERAL_FUNCTIONS.checkIfParentQuestionCheckboxTickedToEnableQuestion = (
    findParentQuestion?: QuestionSchema,
    values?: any,
): Boolean | undefined => {
    return (
        findParentQuestion &&
        findParentQuestion.questionTypeId === ids.questionTypeIdCheckboxId &&
        values &&
        ((Array.isArray(values[findParentQuestion.code]) &&
            values[findParentQuestion.code].includes('on')) ||
            (typeof values[findParentQuestion.code] == 'boolean' &&
                values[findParentQuestion.code] === true))
    );
};

GENERAL_FUNCTIONS.checkIfParentQuestionCheckboxUntickedToClearChildQuestionValue =
    (
        findParentQuestion?: QuestionSchema,
        values?: any,
    ): Boolean | undefined => {
        return (
            findParentQuestion &&
            findParentQuestion.questionTypeId ===
            ids.questionTypeIdCheckboxId &&
            values &&
            ((Array.isArray(values[findParentQuestion.code]) &&
                !values[findParentQuestion.code].includes('on')) ||
                (typeof values[findParentQuestion.code] == 'boolean' &&
                    values[findParentQuestion.code] === false))
        );
    };

interface TermsAndConditionsResponse {
    title: string;
    body: string;
    languageModalBody: string;
    languageModalButtonContinueText: string;
    languageModalButtonSwitchText: string;
    languageModalTitle: string;
}

GENERAL_FUNCTIONS.fetchTermsAndConditions = async (selectdLanguage: string) => {
    const token = store.getState().applicationUser.accessToken;
    const userId = store.getState().applicationUser.applicationUserDetails?.id;

    const result = await fetch(
        `${GENERAL_FUNCTIONS.getBaseURL()}api/ApplicationUser/${userId}/get-terms-and-conditions-login/${selectdLanguage}`,
        {
            method: 'GET',
            headers: HEADER_FUNCTIONS.headersWithTokenJson(token),
        },
    );
    return await result.json();
};

GENERAL_FUNCTIONS.validateRequiredDateInputs = () => {
    const inputs = document.querySelectorAll(
        'input[id$="_date_input"][required]',
    ) as NodeListOf<HTMLInputElement>;
    inputs.forEach((input: any) => {
        if (!input.value) {
            input.setCustomValidity(i18n.t('requiredField'));
        } else {
            input.setCustomValidity('');
        }

        input.oninvalid = function () {
            this.setCustomValidity(i18n.t('requiredField'));
        };

        input.oninput = function () {
            this.setCustomValidity('');
        };
    });
};

GENERAL_FUNCTIONS.formatNumbers = (question: any): string => {
    if (
        question.amount != null &&
        question.amount !== undefined &&
        question.amount !== ''
    ) {
        let currency = 'GBP'; // Default

        // If currencyId is present, use it; otherwise, set by locale
        if (
            question.currencyId &&
            typeof question.currencyId === 'string' &&
            question.currencyId.trim() !== ''
        ) {
            const currencyId = question.currencyId.toLowerCase();
            if (currencyId === codes.currencyIdGBP) {
                currency = 'GBP';
            } else if (currencyId === codes.currencyIdEUR) {
                currency = 'EUR';
            } else if (currencyId === codes.currencyIdCAD) {
                currency = 'CAD';
            }
        } else {
            if (
                i18n.language &&
                (i18n.language.toLowerCase().startsWith('fr-ca') ||
                    i18n.language.toLowerCase().startsWith('en-ca'))
            ) {
                currency = 'CAD';
            } else {
                currency = 'GBP';
            }
        }

        const formattedAmount = new Intl.NumberFormat(i18n.language, {
            style: 'currency',
            currency,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(Number(question.amount));

        return formattedAmount;
    }
    return question._parsedAnswerTextValue;
};
GENERAL_FUNCTIONS.formattedDate = (
    date: any,
    splitter: string = '/',
): string => {
    if (!date) {
        return '';
    }
    const dateFormatRegex = /^\d{2}\/\d{2}\/\d{4}$/;
    if (dateFormatRegex.test(date)) {
        return date;
    }
    const d = new Date(date);
    const day = d.getDate().toString().padStart(2, '0');
    const month = (d.getMonth() + 1).toString().padStart(2, '0');
    const year = d.getFullYear();

    return [day, month, year].join(splitter);
};
GENERAL_FUNCTIONS.formattedTime = (
    date: any,
    splitter: string = ':',
): string => {
    if (!date) {
        return '';
    }
    const d = new Date(date);
    const hours = d.getHours().toString().padStart(2, '0');
    const minutes = d.getMinutes().toString().padStart(2, '0');

    return [hours, minutes].join(splitter);
};

GENERAL_FUNCTIONS.getCurrencySymbol = (currencyId: string): string => {
    const normalisedCurrencyId =
        typeof currencyId === 'string' ? currencyId.toLowerCase() : currencyId;

    if (normalisedCurrencyId === ids.currencyIdGBP) {
        return '£';
    } else if (normalisedCurrencyId === ids.currencyIdEUR) {
        return '€';
    } else if (normalisedCurrencyId === ids.currencyIdCAD) {
        return '$';
    }
    return '';
};

export default GENERAL_FUNCTIONS;
